import { ActionBuilder } from "portal/components/atk/activities/ActionBuilder";
import { ActionViewModel } from "portal/components/atk/activities/view-models/ActionViewModel";
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Link,
  Paper,
  Typography,
} from "@mui/material";
import { atkEquipmentApi } from "common/state/portalApi/atkEquipment";
import { autonomousActivityToString } from "common/utils/enumTranslations";
import { AutonomousActivityType } from "protos/atk/enums";
import { Autotractor } from "protos/atk/autotractor";
import { CarbonLoadingSpinner } from "portal/components/CarbonLoadingSpinner";
import { ConfirmationDialog } from "common/components/ConfirmationDialog";
import { EffectiveConfigValidationMode } from "protos/atk/effective_config";
import { effectorFinalActionIndexes } from "portal/components/atk/activities/view-models/utils";
import { Formik, FormikConfig, useFormikContext } from "formik";
import { FormikDimensionedNumberInput } from "common/components/FormikDimensionedNumberInput";
import { getRigDetailsPath } from "portal/utils/routing";
import {
  rigActivitiesConfigToViewModel,
  RigActivitiesFormViewModel,
  rigActivitiesViewModelToConfig,
  RigActivityConfigViewModel,
  StructuredActionsViewModel,
} from "portal/components/atk/RigActivitiesFormViewModel";
import { RolloutChip } from "portal/components/RolloutChip";
import { rtcJobsApi } from "common/state/rtcJobsApi";
import { Trans, useTranslation } from "react-i18next";
import { typedPath } from "typed-path";
import { TypedPathTo, useFormikValuesAtBasePath } from "portal/utils/typedPath";
import {
  useMutationPopups,
  useQueryPopups,
} from "portal/utils/hooks/useApiPopups";
import { useSelf } from "portal/state/store";
import { useUnsavedChangesBlocker } from "portal/components/unsavedChanges/useUnsavedChangesBlocker";
import EditIcon from "@mui/icons-material/Edit";
import ErrorIcon from "@mui/icons-material/Error";
import React, { useCallback, useMemo, useState } from "react";

const S = typedPath<RigActivitiesFormViewModel>();

const ConstraintsEditForm: React.FC<{
  basePath: TypedPathTo<RigActivityConfigViewModel>;
}> = ({ basePath }) => {
  const { t } = useTranslation();
  const { measurementSystem } = useSelf();

  return (
    <Paper
      variant="outlined"
      className="col-span-full rounded-xl p-4 grid grid-cols-3 gap-2"
    >
      <FormikDimensionedNumberInput
        label={t(
          "views.autotractor.autonomousActivities.action.parameterName.maxHitchAngle"
        )}
        name={basePath.constraints.maxHitchAngleRad.$path}
        dimensionedNumberInputProps={{
          storedUnit: "rad",
          displayUnits: {
            imperial: "deg",
            metric: "deg",
          },
          minStoredValue: 0,
          localSystem: measurementSystem,
        }}
      />
      <FormikDimensionedNumberInput
        label={t(
          "views.autotractor.autonomousActivities.action.parameterName.minTurningRadius"
        )}
        name={basePath.constraints.minTurnRadiusM.$path}
        dimensionedNumberInputProps={{
          storedUnit: "m",
          displayUnits: {
            imperial: "ft",
            metric: "m",
          },
          minStoredValue: 0,
          localSystem: measurementSystem,
        }}
      />
      <FormikDimensionedNumberInput
        label={t(
          "views.autotractor.autonomousActivities.action.parameterName.transitionTime"
        )}
        name={basePath.quickActionTimeSec.$path}
        tooltipText={t(
          "views.autotractor.autonomousActivities.action.parameterName.transitionTimeTooltip"
        )}
        dimensionedNumberInputProps={{
          storedUnit: "s",
          displayUnits: {
            imperial: "s",
            metric: "s",
          },
          minStoredValue: 0,
          localSystem: measurementSystem,
        }}
      />
      <FormikDimensionedNumberInput
        label={t(
          "views.autotractor.autonomousActivities.action.parameterName.targetSpeed"
        )}
        name={basePath.constraints.workingSpeedMps.$path}
        dimensionedNumberInputProps={{
          storedUnit: "m/s",
          displayUnits: {
            imperial: "mph",
            metric: "km/h",
          },
          minStoredValue: 0,
          localSystem: measurementSystem,
        }}
        className="col-start-1"
      />
      <FormikDimensionedNumberInput
        label={t(
          "views.autotractor.autonomousActivities.action.parameterName.turningSpeed"
        )}
        name={basePath.constraints.turningSpeedMps.$path}
        dimensionedNumberInputProps={{
          storedUnit: "m/s",
          displayUnits: {
            imperial: "mph",
            metric: "km/h",
          },
          minStoredValue: 0,
          localSystem: measurementSystem,
        }}
      />
      <FormikDimensionedNumberInput
        label={t(
          "views.autotractor.autonomousActivities.action.parameterName.minSpeed"
        )}
        name={basePath.constraints.transitionSpeedMps.$path}
        dimensionedNumberInputProps={{
          storedUnit: "m/s",
          displayUnits: {
            imperial: "mph",
            metric: "km/h",
          },
          minStoredValue: 0,
          localSystem: measurementSystem,
        }}
      />
    </Paper>
  );
};

const ActivityEditorModal: React.FC<{
  open: boolean;
  onClose: () => void;
  activity: AutonomousActivityType;
  activityBasePath: TypedPathTo<RigActivityConfigViewModel>;
}> = ({ open, onClose, activity, activityBasePath }) => {
  const { t } = useTranslation();
  const activityViewModel = useFormikValuesAtBasePath(activityBasePath);
  const { values, submitForm, setFieldValue, setFieldTouched, dirty } =
    useFormikContext<RigActivitiesFormViewModel>();
  const [editingActionIndex, setEditingActionIndex] = useState<
    number | undefined
  >();
  const editingAction:
    | {
        viewModel: ActionViewModel<unknown>;
        basePath: TypedPathTo<ActionViewModel<unknown>>;
      }
    | undefined = useMemo(
    () =>
      editingActionIndex !== undefined
        ? {
            viewModel: activityViewModel.actions[editingActionIndex]!,
            basePath: activityBasePath.actions[editingActionIndex]!,
          }
        : undefined,
    [activityBasePath.actions, activityViewModel.actions, editingActionIndex]
  );
  const [confirmDeleteAction, setConfirmDeleteAction] =
    useState<boolean>(false);
  const [confirmCancel, setConfirmCancel] = useState<boolean>(false);
  const {
    isBlocked: isNavigationBlocked,
    proceed: proceedWithNavigation,
    reset: resetBlockedNavigation,
  } = useUnsavedChangesBlocker(dirty);

  const onUpdateActions = useCallback(
    (
      nextActions: ActionViewModel<unknown>[],
      deletedActionIndexes: number[]
    ) => {
      // if the user is editing an action that's being deleted, stop editing it
      if (
        editingActionIndex !== undefined &&
        deletedActionIndexes.includes(editingActionIndex)
      ) {
        setEditingActionIndex(undefined);
      }

      // react uses object identity to trigger rerenders; if our caller just
      // overwrote the contents of the actions object, copy it and reassign the
      // value to the copy so we get a rerender
      if (activityViewModel.actions === nextActions) {
        const copiedNextActions = [...nextActions];
        setFieldValue(activityBasePath.actions.$path, copiedNextActions);
      } else {
        setFieldValue(activityBasePath.actions.$path, nextActions);
      }

      setFieldTouched(activityBasePath.actions.$path, true);
    },
    [
      activityBasePath.actions.$path,
      activityViewModel.actions,
      editingActionIndex,
      setFieldTouched,
      setFieldValue,
    ]
  );

  const onDeleteAction = useCallback(() => {
    if (editingActionIndex === undefined) {
      return;
    }

    // without creating a new array and setting the value of actions to it,
    // the structured actions model will still refer to the now-deleted
    // action, causing stale data or a crash
    const nextActions = [...activityViewModel.actions];
    nextActions.splice(editingActionIndex, 1);

    setFieldValue(activityBasePath.actions.$path, nextActions);
    setFieldTouched(activityBasePath.actions.$path, true);

    setEditingActionIndex(undefined);
  }, [
    activityBasePath.actions.$path,
    activityViewModel.actions,
    editingActionIndex,
    setFieldTouched,
    setFieldValue,
  ]);

  const onCancel = useCallback(() => {
    if (dirty) {
      setConfirmCancel(true);
    } else {
      onClose();
    }
  }, [dirty, onClose]);

  const onSave = useCallback(() => {
    setFieldValue(activityBasePath.present.$path, true);
    setFieldTouched(activityBasePath.present.$path, true);
    submitForm();
  }, [
    activityBasePath.present.$path,
    setFieldTouched,
    setFieldValue,
    submitForm,
  ]);

  const structuredModel: StructuredActionsViewModel = useMemo(() => {
    const efai = effectorFinalActionIndexes(activityViewModel.actions);

    // figure out whether each action renders in each stage; this needs to be
    // done separately from + after figuring out whether each action is the
    // final action on its effector because that can influence its rendering
    // (e.g. set-position actions render in fewer stages if they aren't the
    // final action on their effector)
    const actionIndexesRenderingInStageIndex = new Map<number, number[]>();

    for (
      let actionIndex = 0;
      actionIndex < activityViewModel.actions.length;
      actionIndex++
    ) {
      const action = activityViewModel.actions[actionIndex]!;

      for (
        let stageIndex = 0;
        stageIndex < activityViewModel.nStages;
        stageIndex++
      ) {
        if (
          action.actionCardTitle(
            t,
            values.rigEffectiveConfig,
            stageIndex,
            efai.has(actionIndex)
          )
        ) {
          // this action renders; add it to the list
          const existingActionsThisStage =
            actionIndexesRenderingInStageIndex.get(stageIndex) ?? [];
          existingActionsThisStage.push(actionIndex);

          actionIndexesRenderingInStageIndex.set(stageIndex, [
            ...existingActionsThisStage,
          ]);
        }
      }
    }

    return {
      actionIndexesRenderingInStageIndex,
      effectorFinalActionIndexes: efai,
    };
  }, [
    activityViewModel.actions,
    activityViewModel.nStages,
    t,
    values.rigEffectiveConfig,
  ]);

  return (
    <>
      {(isNavigationBlocked || confirmCancel) && (
        <ConfirmationDialog
          title="Lose unsaved changes?"
          onClose={() => {
            if (isNavigationBlocked) {
              resetBlockedNavigation();
            } else {
              setConfirmCancel(false);
            }
          }}
          noText={t("utils.booleans.yes.false")}
          onYes={() => {
            if (isNavigationBlocked) {
              proceedWithNavigation();
            } else {
              onClose();
            }
          }}
          yesText={t("utils.booleans.yes.true")}
        />
      )}
      <Dialog fullWidth maxWidth="lg" open={open} onClose={onClose}>
        <DialogTitle>
          {t("views.autotractor.autonomousActivities.action.editorTitle", {
            activity: autonomousActivityToString(t, activity),
          })}
        </DialogTitle>
        <DialogContent className="grid grid-cols-2 p-4 gap-2">
          <ConstraintsEditForm basePath={activityBasePath} />
          <ActionBuilder
            activityBasePath={activityBasePath}
            structuredModel={structuredModel}
            onEditAction={(actionIndex) => setEditingActionIndex(actionIndex)}
            onUpdateActions={onUpdateActions}
          />
          {editingAction !== undefined && editingActionIndex !== undefined && (
            <Paper
              variant="outlined"
              className="flex flex-col h-full gap-4 rounded-xl p-4"
            >
              <Typography variant="h6">
                {editingAction.viewModel.actionCardTitle(
                  t,
                  values.rigEffectiveConfig,
                  editingAction.viewModel.startInStageIndex,
                  structuredModel.effectorFinalActionIndexes.has(
                    editingActionIndex
                  )
                )}
              </Typography>
              <Divider />
              <editingAction.viewModel.components.EditForm
                basePath={editingAction.basePath}
              />
              <div className="flex-1" />
              <Button
                color="error"
                variant="contained"
                className="w-1/3 self-center"
                onClick={() => setConfirmDeleteAction(true)}
              >
                {t("utils.actions.delete")}
              </Button>
            </Paper>
          )}
          {confirmDeleteAction && (
            <ConfirmationDialog
              onClose={() => setConfirmDeleteAction(false)}
              title="Delete action?"
              description="If you delete this action, it will be removed from this task operation."
              noText={t("utils.actions.cancel")}
              yesText={t("utils.actions.delete")}
              onYes={onDeleteAction}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            color="secondary"
            onClick={onCancel}
            className="w-1/6"
          >
            {t("utils.actions.cancel")}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={onSave}
            disabled={!dirty}
            className="w-1/6"
          >
            {t("utils.actions.save")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const _M6ActivitiesConfigEditors: React.FC<{
  activities: AutonomousActivityType[];
  rig: Autotractor | undefined;
  viewingActivity: AutonomousActivityType | undefined;
  setViewingActivity: (activity: AutonomousActivityType | undefined) => void;
  resetConfig: () => void;
}> = ({
  activities,
  rig,
  viewingActivity,
  setViewingActivity,
  resetConfig,
}) => {
  const { t } = useTranslation();
  const { values } = useFormikContext<RigActivitiesFormViewModel>();
  const currentActivitiesConfig = rigActivitiesViewModelToConfig(values);

  // patch the rig with the currently configured action overrides and send it to
  // the prevalidation endpoint to figure out whether there are any validation
  // problems with the action overrides relative to the rig
  const patchedRig = useMemo(() => {
    if (!rig?.controls) {
      return;
    }
    return {
      ...rig,
      controls: {
        ...rig.controls,
        activities: currentActivitiesConfig,
      },
    };
  }, [currentActivitiesConfig, rig]);
  const {
    data: patchedRigPrevalidationResult,
    isLoading: isPatchedRigValidating,
  } = useQueryPopups(
    atkEquipmentApi.endpoints.prevalidateRig.useQuery(
      {
        rig: patchedRig!,
      },
      { skip: !patchedRig }
    )
  );
  const invalidActivities = useMemo(() => {
    const activities = new Set<AutonomousActivityType>();

    if (!patchedRigPrevalidationResult) {
      return;
    }

    for (const err of patchedRigPrevalidationResult.errors) {
      if (err.activity) {
        activities.add(err.activity);
      }
    }

    return activities;
  }, [patchedRigPrevalidationResult]);

  return (
    <>
      {invalidActivities?.size ? (
        <Alert severity="error">
          {t("views.autotractor.jobDetails.taskOperationsNeedUpdate")}
        </Alert>
      ) : undefined}
      <Typography variant="subtitle1" className="flex flex-row items-center">
        {t("views.autotractor.jobDetails.taskOperationsGroup")}
        <RolloutChip variant="6.5+" />
        {isPatchedRigValidating && (
          <CircularProgress className="w-6 h-6 ml-2" />
        )}
      </Typography>
      {viewingActivity && (
        <ActivityEditorModal
          activity={viewingActivity}
          activityBasePath={S.activities[viewingActivity]!}
          open={true}
          onClose={() => {
            resetConfig();
            setViewingActivity(undefined);
          }}
        />
      )}
      {activities.map((activity) => (
        <Button
          key={activity}
          className="justify-start"
          color="secondary"
          variant="contained"
          startIcon={<EditIcon />}
          onClick={() => setViewingActivity(activity)}
          endIcon={invalidActivities?.has(activity) ? <ErrorIcon /> : undefined}
        >
          {autonomousActivityToString(t, activity)}
        </Button>
      ))}
      <Typography variant="subtitle1" className="flex flex-row items-center">
        {t("views.autotractor.jobDetails.stateChangeGroup")}
        <RolloutChip variant="6.5+" />
        {isPatchedRigValidating && (
          <CircularProgress className="w-6 h-6 ml-2" />
        )}
      </Typography>
      {[
        AutonomousActivityType.ACTIVITY_STARTUP,
        AutonomousActivityType.ACTIVITY_SHUTDOWN,
        AutonomousActivityType.ACTIVITY_PAUSE,
      ].map((activity) => (
        <Button
          key={activity}
          className="justify-start"
          color="secondary"
          variant="contained"
          startIcon={<EditIcon />}
          onClick={() => setViewingActivity(activity)}
          endIcon={invalidActivities?.has(activity) ? <ErrorIcon /> : undefined}
        >
          {autonomousActivityToString(t, activity)}
        </Button>
      ))}
    </>
  );
};

export const M6ActivitiesConfigEditors: React.FC<{
  jobId: number;
  activities: AutonomousActivityType[];
}> = ({ jobId, activities }) => {
  const { t } = useTranslation();
  const { data: configuredJob, isLoading: isJobLoading } = useQueryPopups(
    rtcJobsApi.endpoints.getConfiguredJob.useQuery(
      { id: jobId.toString() },
      { skip: !jobId }
    )
  );
  const { data: getRigResultNoActivities, isLoading: isNoActionsRigLoading } =
    useQueryPopups(
      atkEquipmentApi.endpoints.getRig.useQuery(
        {
          id: configuredJob?.groundPrep?.rigId ?? "",
          validate: true,
          validationMode:
            EffectiveConfigValidationMode.VALIDATION_MODE_NO_ACTIVITIES,
        },
        { skip: !configuredJob?.groundPrep?.rigId }
      )
    );
  const { data: currentActivitiesResult, isLoading: isActivityConfigLoading } =
    useQueryPopups(
      rtcJobsApi.endpoints.getRigActivitiesConfiguration.useQuery({
        jobId: jobId.toString(),
      })
    );
  const [updateActivities] = useMutationPopups(
    rtcJobsApi.endpoints.setRigActivitiesConfiguration.useMutation()
  );
  const [viewingActivity, setViewingActivity] = useState<
    AutonomousActivityType | undefined
  >();

  const onSubmit: FormikConfig<RigActivitiesFormViewModel>["onSubmit"] =
    useCallback(
      async (values) => {
        const result = await updateActivities({
          jobId: jobId.toString(),
          config: rigActivitiesViewModelToConfig(values),
        });

        if (!result.error) {
          setViewingActivity(undefined);
        }
      },
      [jobId, updateActivities]
    );

  const body: JSX.Element = useMemo(() => {
    if (isJobLoading || isNoActionsRigLoading || isActivityConfigLoading) {
      return <CarbonLoadingSpinner />;
    } else if (!configuredJob?.groundPrep?.rigId) {
      // this element shouldn't be rendered if the job isn't a ground prep job,
      // and it won't work for non-ground prep jobs, so just throw a warning and
      // return nothing
      console.error(
        "M6ActivitiesConfigEditors: invoked for non-ground prep job"
      );
      return <></>;
    } else if (!getRigResultNoActivities?.validationResult?.effectiveConfig) {
      // there's some structural validation failure on this job's rig; it's
      // impossible to predict what actions are available or what they would do
      // if configured.  redirect the user to resolve the validation failure.
      return (
        <>
          <Typography variant="subtitle1">
            {t("views.autotractor.jobDetails.taskOperationsGroup")}
            <RolloutChip variant="6.5+" />
          </Typography>
          <Alert severity="warning">
            <Trans
              i18nKey="views.autotractor.jobDetails.equipmentValidationFailed"
              components={[
                <Link
                  key={0}
                  href={getRigDetailsPath(configuredJob.groundPrep.rigId)}
                />,
              ]}
            />
          </Alert>
        </>
      );
    } else {
      return (
        <Formik
          initialValues={rigActivitiesConfigToViewModel(
            getRigResultNoActivities.validationResult.effectiveConfig,
            currentActivitiesResult
          )}
          onSubmit={onSubmit}
          enableReinitialize
        >
          {(props) => (
            <_M6ActivitiesConfigEditors
              activities={activities}
              rig={getRigResultNoActivities.rig}
              viewingActivity={viewingActivity}
              setViewingActivity={setViewingActivity}
              resetConfig={props.resetForm}
            />
          )}
        </Formik>
      );
    }
  }, [
    activities,
    configuredJob?.groundPrep?.rigId,
    currentActivitiesResult,
    getRigResultNoActivities?.rig,
    getRigResultNoActivities?.validationResult?.effectiveConfig,
    isActivityConfigLoading,
    isJobLoading,
    isNoActionsRigLoading,
    onSubmit,
    t,
    viewingActivity,
  ]);

  return <div className="p-2 flex flex-col gap-2">{body}</div>;
};
